import { Check, ChevronDown, ChevronRight, X } from "lucide-react";
import type { Quest } from "./quests";

export default function GettingStartedChecklist({
  quests,
  done,
  open,
  onToggle,
  onPick,
  onDismiss,
}: {
  quests: Quest[];
  done: Set<string>;
  open: boolean;
  onToggle: () => void;
  onPick: (quest: Quest) => void;
  onDismiss: () => void;
}) {
  const count = quests.filter((q) => done.has(q.id)).length;
  const total = quests.length;
  const allDone = count === total;
  const pct = total ? count / total : 1;

  // Progress ring for the closed pill.
  const R = 9;
  const C = 2 * Math.PI * R;

  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2.5 rounded-full border border-[#d6ebe3] bg-white py-2 pl-2.5 pr-4 text-[13px] font-semibold text-[#1e302a] shadow-[0_12px_32px_-10px_rgba(5,25,18,0.35)] transition-transform hover:-translate-y-0.5"
        aria-label={`Getting started, ${count} of ${total} done`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r={R} fill="none" stroke="#e3ece8" strokeWidth="3" />
          <circle
            cx="12"
            cy="12"
            r={R}
            fill="none"
            stroke="#218760"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - pct)}
            transform="rotate(-90 12 12)"
            style={{ transition: "stroke-dashoffset 400ms ease" }}
          />
        </svg>
        <span>{allDone ? "All done 🏆" : "Getting started"}</span>
        <span className="tabular-nums text-[#697571]">
          {count}/{total}
        </span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] w-[340px] overflow-hidden rounded-[18px] border border-[#d6ebe3] bg-white shadow-[0_24px_60px_-12px_rgba(5,25,18,0.35)]"
      role="region"
      aria-label="Getting started checklist"
    >
      <div className="bg-gradient-to-br from-[#edf7f2] to-white px-4 pb-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[15px] font-semibold tracking-[-0.3px] text-[#1e302a]">
              {allDone ? "You did it! 🏆" : "Your first-day quests 🚀"}
            </p>
            <p className="mt-0.5 text-[12px] leading-[1.45] text-[#697571]">
              {allDone
                ? "Every quest ticked. You're officially a Xale pro."
                : "Small steps, big wins. Tick them all for eternal glory."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={onToggle}
              aria-label="Minimise"
              className="flex size-7 items-center justify-center rounded-lg text-[#697571] hover:bg-[#f1f6f4] hover:text-[#1e302a]"
            >
              <ChevronDown className="size-4" />
            </button>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close the checklist for good"
              className="flex size-7 items-center justify-center rounded-lg text-[#697571] hover:bg-[#f1f6f4] hover:text-[#1e302a]"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#e3ece8]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#2fa877] to-[#156548] transition-[width] duration-500"
              style={{ width: `${Math.round(pct * 100)}%` }}
            />
          </div>
          <span className="text-[11px] font-medium tabular-nums text-[#697571]">
            {count}/{total}
          </span>
        </div>
      </div>

      <ul className="max-h-[50vh] overflow-y-auto px-2 py-2">
        {quests.map((quest) => {
          const isDone = done.has(quest.id);
          return (
            <li key={quest.id}>
              <button
                type="button"
                onClick={() => onPick(quest)}
                className="group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-[#f5faf8]"
              >
                <span
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full text-[14px] ${
                    isDone ? "bg-[#218760] text-white" : "bg-[#f1f6f4]"
                  }`}
                  aria-hidden
                >
                  {isDone ? <Check className="size-4" strokeWidth={3} /> : quest.emoji}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-[13px] font-medium ${
                      isDone ? "text-[#697571] line-through decoration-[#9fb3ab]" : "text-[#1e302a]"
                    }`}
                  >
                    {quest.label}
                  </span>
                  <span className="block truncate text-[11.5px] text-[#8a9691]">{quest.hint}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-[#b5c2bd] transition-transform group-hover:translate-x-0.5 group-hover:text-[#218760]" />
              </button>
            </li>
          );
        })}
      </ul>

      {allDone && (
        <div className="border-t border-[#eef3f1] px-4 py-3">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full rounded-xl bg-[#218760] py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#156548]"
          >
            Close the checklist
          </button>
        </div>
      )}
    </div>
  );
}
